import type { ImageValue } from '@elfsight//configurator-contracts';
import type {
  DerivedTableItemProps,
  TableItemValue,
  TableProps
} from '@elfsight/widget-checkout';
import { INVALID_VALUE_DASH } from '@elfsight/widget-checkout';
import type {
  FieldValue,
  UploadedFileType
} from '@elfsight/widget-form-builder/widget';
import { useFormSelector } from '@elfsight/widget-form-builder/widget';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import {
  isChoiceField,
  isDropdownField,
  isHeadingField,
  isImageChoiceField,
  isNumberField,
  isSliderField
} from '../../../guards';
import type {
  CalculatorField,
  ChoiceType,
  DropdownType,
  ImageChoiceType
} from '../../../types';
import { useCalculatorConditionalLogic } from '../../conditional-logic-context';
import { formatCalculatorValue, processCalculatorValue } from '../../utils';

export function useInputsSummary() {
  const { t } = useLingui();
  const { thousandsSeparator, decimalSeparator } = useSettings();
  const { displayedFields } = useCalculatorConditionalLogic();
  const { fields } = useFormSelector();

  const table: TableProps = {
    title: t`Your Inputs`,
    layout: 'horizontal',
    items: []
  };

  let tableHasImages = false;
  const inlineValuesItemsCandidates: DerivedTableItemProps[] = [];

  displayedFields.forEach((field) => {
    if (isHeadingField(field)) {
      return;
    }

    const tableItem: DerivedTableItemProps = {
      label: field.label,
      values: [],
      withInlineValues: false
    };

    const rawValue = getFieldValue(field, fields);

    if (isNumberField(field) || isSliderField(field)) {
      const processedValue = processCalculatorValue(
        field.format,
        rawValue?.toString()
      );

      const formattedValue = formatCalculatorValue({
        value: processedValue,
        format: field.format,
        thousandsSeparator,
        decimalSeparator
      });

      tableItem.values.push({ value: formattedValue });
    }

    const isFieldWithOptions =
      isChoiceField(field) ||
      isImageChoiceField(field) ||
      isDropdownField(field);

    if (isFieldWithOptions) {
      const itemValues = createTableItemValuesFromField({
        field,
        fieldValue: rawValue
      });

      const withImages = itemValues.some((value) => !!value?.imageSrc);

      if (withImages) {
        tableHasImages = true;
      } else {
        inlineValuesItemsCandidates.push(tableItem);
      }

      tableItem.values.push(...itemValues);
    }

    table.items.push(tableItem);
  });

  if (tableHasImages) {
    table.layout = 'vertical';
    inlineValuesItemsCandidates.forEach((item) => {
      item.withInlineValues = true;
    });
  }

  return table;
}

function getFieldValue(
  settingsField: CalculatorField,
  formSelectorFields: ReturnType<typeof useFormSelector>['fields']
) {
  if (isHeadingField(settingsField) || !settingsField.id) {
    return undefined;
  }

  const value = formSelectorFields[settingsField.id]?.value;

  if (
    isChoiceField(settingsField) ||
    (isImageChoiceField(settingsField) && settingsField.multiple)
  ) {
    return value ?? settingsField.initialValueMultiple ?? undefined;
  }

  return value ?? settingsField.initialValue ?? undefined;
}

function createTableItemValuesFromField({
  field,
  fieldValue
}: {
  field: DropdownType | ChoiceType | ImageChoiceType;
  fieldValue: FieldValue | undefined;
}) {
  if (Array.isArray(fieldValue)) {
    if (fieldValue.length === 0) {
      return [getInvalidTableItemValue()];
    }

    return fieldValue.map((value) =>
      createTableItemValueFromOptions({
        targetOptionId: value,
        options: field.options
      })
    );
  }

  return [
    createTableItemValueFromOptions({
      targetOptionId: fieldValue,
      options: field.options
    })
  ];
}

function createTableItemValueFromOptions({
  options,
  targetOptionId
}: {
  targetOptionId: string | UploadedFileType | number | undefined;
  options: { id?: string; name: string; image?: ImageValue }[];
}) {
  const result: TableItemValue = getInvalidTableItemValue();

  if (typeof targetOptionId !== 'string') {
    return result;
  }

  const targetOption = findOptionById(targetOptionId, options);

  if (targetOption) {
    result.value = targetOption.name;
  }

  if (targetOption?.image?.url) {
    result.imageSrc = targetOption.image.url;
  }

  return result;
}

function getInvalidTableItemValue(): TableItemValue {
  return { value: INVALID_VALUE_DASH };
}

function findOptionById(
  targetOptionId: string,
  options: { id?: string; name: string; image?: ImageValue }[]
) {
  return options.find((option) => option.id === targetOptionId);
}
